/**
 * The home page's two brand colours. Every colour on the page (lens-ui tokens,
 * the drawing ink, the 3D part, the backdrop) derives from them.
 */

export type Oklch = { l: number; c: number; h: number };
export type Rgb = [number, number, number];

/** The primary action colour. */
const red: Oklch = { l: 0.54, c: 0.215, h: 25 };
/** The dark ink the blue ramp is built on. */
const ink: Oklch = { l: 0.21, c: 0.085, h: 264 };

/** Peak chroma of the blue ramp's mid steps. */
const rampChroma = 0.19;

/** Lightness of the blue ramp steps and their share of `rampChroma`; step 900 is the ink itself. */
const blueSteps: [step: number, l: number, share: number][] = [
	[50, 0.975, 0.05],
	[100, 0.95, 0.1],
	[200, 0.9, 0.24],
	[300, 0.82, 0.42],
	[400, 0.7, 0.79],
	[500, 0.58, 1],
	[600, 0.52, 1],
	[700, 0.47, 0.97],
	[800, 0.4, 0.88],
];

/** Neutral tokens as [name, l, c], in the ink's hue. `--surface` is the section cards' fill. */
const neutrals: [name: string, l: number, c: number][] = [
	["--surface", 0.974, 0.003],
	["--muted", 0.95, 0.008],
	["--border", 0.86, 0.012],
	["--input", 0.66, 0.012],
	["--ring", 0.66, 0.012],
	["--muted-foreground", 0.4, 0.016],
	["--foreground", 0.205, 0.02],
];

const blue: Record<number, Oklch> = { 900: ink };
for (const [step, l, share] of blueSteps) blue[step] = { l, c: rampChroma * share, h: ink.h };

export const palette: {
	red: Oklch;
	redHover: Oklch;
	redActive: Oklch;
	/** Keyed by ramp step (50..800), plus 900 = ink. */
	blue: Record<number, Oklch>;
	paper: Oklch;
} = {
	red,
	redHover: { l: red.l - 0.045, c: red.c * 0.975, h: red.h },
	redActive: { l: red.l - 0.095, c: red.c * 0.88, h: red.h },
	blue,
	paper: { l: 1, c: 0, h: 0 },
};

const oklchCss = (c: Oklch) => `oklch(${c.l.toFixed(3)} ${c.c.toFixed(3)} ${c.h.toFixed(1)})`;

const tokens: [name: string, color: Oklch][] = [
	["--primary", palette.red],
	["--primary-hover", palette.redHover],
	["--primary-active", palette.redActive],
	["--ink", ink],
	["--ink-soft", { ...ink, l: ink.l + 0.08 }],
	...blueSteps.map(([step]): [string, Oklch] => [`--blue-${step}`, blue[step]]),
	...[100, 200, 300, 400, 500, 600, 700, 800].map((step): [string, Oklch] => [`--secondary-${step}`, blue[step]]),
	...neutrals.map(([name, l, c]): [string, Oklch] => [name, { l, c, h: ink.h }]),
];

/**
 * CSS that overrides the lens-ui tokens with the palette. The doubled `:root`
 * outranks lens-ui's `:root` wherever the two land in `<head>`.
 * @example
 * <svelte:head>{@html `<style>${paletteCss}</style>`}</svelte:head>
 */
export const paletteCss = `:root:root{${tokens.map(([name, c]) => `${name}:${oklchCss(c)}`).join(";")}}`;

/**
 * `c` as gamma-encoded sRGB in [0, 1], clipped to the sRGB gamut.
 * @example
 * oklchRgb({ l: 1, c: 0, h: 0 }); // about [1, 1, 1]
 */
export function oklchRgb(c: Oklch): Rgb {
	const [r, g, b] = oklchLinear(c);
	return [gammaEncode(r), gammaEncode(g), gammaEncode(b)];
}

function oklchLinear({ l: L, c: C, h: H }: Oklch): Rgb {
	const hr = (H * Math.PI) / 180;
	const a = C * Math.cos(hr);
	const b = C * Math.sin(hr);
	const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
	const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
	const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
	return [
		4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
		-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
		-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
	];
}

function gammaEncode(x: number): number {
	const v = Math.min(1, Math.max(0, x));
	return v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
}
